// ---------------------------------------------------------------------------
// FaceCache — procedural face rendering shared by BOTH renderers.
// The 2D renderer draws these canvases directly; the 3D renderer wraps them
// in CanvasTextures. One drawing codepath = the two modes always agree.
// ---------------------------------------------------------------------------

import type { FaceSpec } from "../shared/defs";
import { BACKGAMMON, bgPointPitch, bgPointX, LORCANA_MAT, MTG_MAT, MTG_MAT_LEGACY, POKE_MAT, TRICK_MAT, type TcgMatSpec } from "../shared/tables";

const PX_PER_UNIT = 40;
/** Image faces (imported card art) render at high resolution for readability. */
const IMAGE_PX_PER_UNIT = 110;

const cache = new Map<string, HTMLCanvasElement>();

export function getFace(spec: FaceSpec, wUnits: number, hUnits: number): HTMLCanvasElement {
  const key = JSON.stringify(spec) + `|${wUnits}x${hUnits}`;
  let c = cache.get(key);
  if (c) return c;
  const px = spec.type === "image" ? IMAGE_PX_PER_UNIT : PX_PER_UNIT;
  c = document.createElement("canvas");
  c.width = Math.round(wUnits * px);
  c.height = Math.round(hUnits * px);
  const g = c.getContext("2d")!;
  drawFace(g, spec, c.width, c.height, px);
  cache.set(key, c);
  return c;
}

function drawFace(g: CanvasRenderingContext2D, spec: FaceSpec, w: number, h: number, pxPerUnit: number): void {
  switch (spec.type) {
    case "proc-card": return drawCardFront(g, w, h, spec.rank, spec.suit);
    case "proc-back": return drawCardBack(g, w, h, spec.style);
    case "proc-chip": return drawChip(g, w, h, spec.color, spec.value);
    case "proc-board": return drawBoard(g, w, h, spec.style, spec.color);
    case "proc-mtgmat":
      return spec.v === 2 ? drawMtgMatV2(g, w, h, spec.color) : drawMtgMat(g, w, h, spec.color);
    case "proc-tcgmat": return drawTcgMat(g, w, h, spec.game);
    case "proc-die": return drawDieFace(g, w, h, spec.sides, spec.value, spec.color, spec.label);
    case "proc-domino": return drawDomino(g, w, h, spec.a, spec.b);
    case "proc-piece": return drawPiece(g, w, h, spec.glyph, spec.color, spec.ink);
    case "color":
      g.fillStyle = spec.color;
      g.fillRect(0, 0, w, h);
      return;
    case "image": {
      // Clip to the art's corner radius — scans bake white/black corners.
      const rPx = (spec.radius ?? 0) * pxPerUnit;
      const clip = () => {
        if (rPx > 0) {
          roundRectPath(g, 0, 0, w, h, rPx);
          g.clip();
        }
      };
      g.save();
      clip();
      g.fillStyle = "#c8c2b6";
      g.fillRect(0, 0, w, h);
      g.restore();
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        g.clearRect(0, 0, w, h);
        g.save();
        clip();
        g.drawImage(img, 0, 0, w, h);
        g.restore();
        bumpFacesVersion(); // renderers + panels refresh their copies
      };
      img.src = spec.url;
      return;
    }
  }
}

/**
 * Monotonic counter bumped when an async image face finishes loading.
 * Consumers (renderers, panels) each track the last version they saw and
 * refresh their own copies/textures when it changes.
 */
let _facesVersion = 1;
export function facesVersion(): number { return _facesVersion; }
function bumpFacesVersion(): void { _facesVersion++; }

// ------------------------------------------------------------------- cards

const SUIT_GLYPH: Record<string, string> = { S: "♠", H: "♥", D: "♦", C: "♣" };
const SUIT_COLOR: Record<string, string> = { S: "#1c1c22", C: "#1c1c22", H: "#c0392b", D: "#c0392b" };

/** Pip layouts in normalized coords: x ∈ [-1,1], y ∈ [-1,1] (y>0 pips flip). */
const PIPS: Record<string, Array<[number, number]>> = {
  A: [[0, 0]],
  "2": [[0, -1], [0, 1]],
  "3": [[0, -1], [0, 0], [0, 1]],
  "4": [[-1, -1], [1, -1], [-1, 1], [1, 1]],
  "5": [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]],
  "6": [[-1, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [1, 1]],
  "7": [[-1, -1], [1, -1], [0, -0.5], [-1, 0], [1, 0], [-1, 1], [1, 1]],
  "8": [[-1, -1], [1, -1], [0, -0.5], [-1, 0], [1, 0], [0, 0.5], [-1, 1], [1, 1]],
  "9": [[-1, -1], [1, -1], [-1, -0.33], [1, -0.33], [0, 0], [-1, 0.33], [1, 0.33], [-1, 1], [1, 1]],
  "10": [[-1, -1], [1, -1], [0, -0.66], [-1, -0.33], [1, -0.33], [-1, 0.33], [1, 0.33], [0, 0.66], [-1, 1], [1, 1]],
};

function roundRectPath(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

function drawCardFront(g: CanvasRenderingContext2D, w: number, h: number, rank: string, suit: string): void {
  const r = w * 0.09;
  roundRectPath(g, 0, 0, w, h, r);
  g.fillStyle = "#fdfdf8";
  g.fill();
  g.strokeStyle = "#d5d2c8";
  g.lineWidth = 2;
  g.stroke();

  const color = SUIT_COLOR[suit]!;
  const glyph = SUIT_GLYPH[suit]!;
  g.fillStyle = color;

  // Corner indices (top-left, and bottom-right rotated 180°).
  const drawCorner = () => {
    g.textAlign = "center";
    g.font = `bold ${w * 0.17}px Georgia, serif`;
    g.textBaseline = "alphabetic";
    g.fillText(rank, w * 0.115, h * 0.115);
    g.font = `${w * 0.15}px serif`;
    g.fillText(glyph, w * 0.115, h * 0.115 + w * 0.15);
  };
  drawCorner();
  g.save();
  g.translate(w, h);
  g.rotate(Math.PI);
  drawCorner();
  g.restore();

  const isFace = rank === "J" || rank === "Q" || rank === "K";
  if (isFace) {
    // Framed letter + suit — clean, readable stand-in for court art.
    const fx = w * 0.2, fy = h * 0.18, fw = w * 0.6, fh = h * 0.64;
    roundRectPath(g, fx, fy, fw, fh, w * 0.04);
    g.strokeStyle = color;
    g.lineWidth = 3;
    g.stroke();
    g.font = `bold ${w * 0.42}px Georgia, serif`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText(rank, w / 2, h * 0.44);
    g.font = `${w * 0.26}px serif`;
    g.fillText(glyph, w / 2, h * 0.66);
  } else {
    const pips = PIPS[rank] ?? [];
    const big = rank === "A";
    const px = w * 0.235, py = h * 0.3; // pip field half-extents
    g.font = `${big ? w * 0.55 : w * 0.21}px serif`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    for (const [nx, ny] of pips) {
      const x = w / 2 + nx * px;
      const y = h / 2 + ny * py;
      if (ny > 0) {
        g.save();
        g.translate(x, y);
        g.rotate(Math.PI);
        g.fillText(glyph, 0, 0);
        g.restore();
      } else {
        g.fillText(glyph, x, y);
      }
    }
  }
}

function drawCardBack(g: CanvasRenderingContext2D, w: number, h: number, style: string): void {
  if (style === "mtg") return drawMtgBack(g, w, h);
  if (style === "poke") return drawPokeBack(g, w, h);
  if (style === "lorcana") return drawLorcanaBack(g, w, h);
  if (style === "bone") return drawBoneBack(g, w, h);
  const base = style === "blue" ? "#2c5f9e" : "#a33327";
  const dark = style === "blue" ? "#234c80" : "#872a20";
  const r = w * 0.09;
  roundRectPath(g, 0, 0, w, h, r);
  g.fillStyle = "#f4f2ea";
  g.fill();
  g.strokeStyle = "#d5d2c8";
  g.lineWidth = 2;
  g.stroke();

  const m = w * 0.07;
  g.save();
  roundRectPath(g, m, m, w - 2 * m, h - 2 * m, r * 0.6);
  g.clip();
  g.fillStyle = base;
  g.fillRect(m, m, w - 2 * m, h - 2 * m);
  // Diamond lattice.
  g.strokeStyle = dark;
  g.lineWidth = 2;
  const step = w * 0.11;
  g.beginPath();
  for (let x = -h; x < w + h; x += step) {
    g.moveTo(x, 0);
    g.lineTo(x + h, h);
    g.moveTo(x + h, 0);
    g.lineTo(x, h);
  }
  g.stroke();
  // Center medallion.
  g.fillStyle = "#f4f2ea";
  g.beginPath();
  g.ellipse(w / 2, h / 2, w * 0.17, w * 0.17, 0, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = base;
  g.font = `${w * 0.2}px serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText("❦", w / 2, h / 2);
  g.restore();
}

/**
 * Pokémon-flavored back: deep blue field with a centered white-ringed ball
 * motif. Original artwork (the official back is copyrighted — never hotlinked).
 */
function drawPokeBack(g: CanvasRenderingContext2D, w: number, h: number): void {
  const r = w * 0.055;
  roundRectPath(g, 0, 0, w, h, r);
  g.fillStyle = "#1c3f7d";
  g.fill();
  const grad = g.createRadialGradient(w / 2, h / 2, w * 0.1, w / 2, h / 2, h * 0.75);
  grad.addColorStop(0, "#2a55a0");
  grad.addColorStop(1, "#152f60");
  g.save();
  roundRectPath(g, w * 0.045, w * 0.045, w - w * 0.09, h - w * 0.09, r * 0.7);
  g.fillStyle = grad;
  g.fill();
  g.clip();
  // Soft diagonal energy streaks.
  g.strokeStyle = "rgba(255,255,255,0.06)";
  g.lineWidth = w * 0.05;
  g.beginPath();
  for (let x = -h; x < w + h; x += w * 0.22) {
    g.moveTo(x, h);
    g.lineTo(x + h * 0.55, 0);
  }
  g.stroke();
  // Centered ball: white ring, dark band, inner button.
  const cx = w / 2, cy = h / 2, R = w * 0.24;
  g.beginPath();
  g.arc(cx, cy, R, 0, Math.PI * 2);
  g.fillStyle = "#f2f0e9";
  g.fill();
  g.beginPath();
  g.arc(cx, cy, R, Math.PI, 0);
  g.closePath();
  g.fillStyle = "#c33b3b";
  g.fill();
  g.strokeStyle = "#20242c";
  g.lineWidth = w * 0.028;
  g.beginPath();
  g.moveTo(cx - R, cy);
  g.lineTo(cx + R, cy);
  g.stroke();
  g.beginPath();
  g.arc(cx, cy, R * 0.32, 0, Math.PI * 2);
  g.fillStyle = "#f2f0e9";
  g.fill();
  g.stroke();
  g.restore();
}

/**
 * Lorcana-flavored back: deep indigo field, gold border and a gold hex/ink
 * medallion. Original artwork, same rule as the others.
 */
function drawLorcanaBack(g: CanvasRenderingContext2D, w: number, h: number): void {
  const r = w * 0.055;
  roundRectPath(g, 0, 0, w, h, r);
  g.fillStyle = "#151233";
  g.fill();
  const m = w * 0.05;
  g.save();
  roundRectPath(g, m, m, w - 2 * m, h - 2 * m, r * 0.7);
  const grad = g.createRadialGradient(w / 2, h / 2, w * 0.08, w / 2, h / 2, h * 0.7);
  grad.addColorStop(0, "#2c2560");
  grad.addColorStop(1, "#171338");
  g.fillStyle = grad;
  g.fill();
  g.strokeStyle = "#c9a84c";
  g.lineWidth = w * 0.016;
  g.stroke();
  g.clip();
  // Faint concentric hexes.
  const hex = (R: number) => {
    g.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      const px = w / 2 + R * Math.cos(a), py = h / 2 + R * Math.sin(a);
      i === 0 ? g.moveTo(px, py) : g.lineTo(px, py);
    }
    g.closePath();
  };
  g.strokeStyle = "rgba(201,168,76,0.22)";
  g.lineWidth = w * 0.012;
  for (const R of [w * 0.42, w * 0.34]) { hex(R); g.stroke(); }
  // Gold medallion with an ink-drop core.
  g.strokeStyle = "#c9a84c";
  g.lineWidth = w * 0.02;
  hex(w * 0.24);
  g.stroke();
  g.fillStyle = "#c9a84c";
  g.beginPath();
  g.arc(w / 2, h / 2, w * 0.1, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = "#171338";
  g.beginPath();
  g.arc(w / 2, h / 2, w * 0.055, 0, Math.PI * 2);
  g.fill();
  g.restore();
}

/**
 * Custom MTG-styled back: dark brown field, tan oval, five-color pentagon.
 * Original artwork (the official card back is copyrighted — never hotlinked).
 */
function drawMtgBack(g: CanvasRenderingContext2D, w: number, h: number): void {
  const r = w * 0.055; // real MTG corners are subtle
  roundRectPath(g, 0, 0, w, h, r);
  g.fillStyle = "#141210";
  g.fill();

  // Brown field with a soft radial light.
  const m = w * 0.045;
  roundRectPath(g, m, m, w - 2 * m, h - 2 * m, r * 0.7);
  const grad = g.createRadialGradient(w / 2, h * 0.42, w * 0.1, w / 2, h / 2, h * 0.72);
  grad.addColorStop(0, "#5b4326");
  grad.addColorStop(0.6, "#46331e");
  grad.addColorStop(1, "#332415");
  g.fillStyle = grad;
  g.fill();
  g.strokeStyle = "#1c150d";
  g.lineWidth = Math.max(2, w * 0.008);
  g.stroke();

  // Inner pinstripe frame.
  const m2 = w * 0.085;
  roundRectPath(g, m2, m2, w - 2 * m2, h - 2 * m2, r * 0.5);
  g.strokeStyle = "rgba(214,186,132,0.55)";
  g.lineWidth = Math.max(1.5, w * 0.006);
  g.stroke();

  // Central tan oval.
  const cx = w / 2, cy = h * 0.5;
  const orx = w * 0.335, ory = h * 0.27;
  g.beginPath();
  g.ellipse(cx, cy, orx, ory, 0, 0, Math.PI * 2);
  const og = g.createRadialGradient(cx, cy - ory * 0.35, orx * 0.15, cx, cy, orx);
  og.addColorStop(0, "#d9c398");
  og.addColorStop(1, "#b39a6b");
  g.fillStyle = og;
  g.fill();
  g.strokeStyle = "#2a1f12";
  g.lineWidth = Math.max(2, w * 0.01);
  g.stroke();
  g.beginPath();
  g.ellipse(cx, cy, orx * 0.9, ory * 0.9, 0, 0, Math.PI * 2);
  g.strokeStyle = "rgba(72,54,30,0.6)";
  g.lineWidth = Math.max(1, w * 0.005);
  g.stroke();

  // Five-color pentagon of mana dots inside the oval.
  const MANA = ["#f5f0da", "#3f76b5", "#3a3532", "#c8422e", "#3e7d4a"]; // W U B R G
  const pr = Math.min(orx, ory) * 0.52;
  const dotR = w * 0.055;
  MANA.forEach((color, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
    const x = cx + Math.cos(a) * pr;
    const y = cy + Math.sin(a) * pr * 1.05;
    g.beginPath();
    g.arc(x, y, dotR, 0, Math.PI * 2);
    g.fillStyle = color;
    g.fill();
    g.strokeStyle = "#1c150d";
    g.lineWidth = Math.max(1.5, w * 0.007);
    g.stroke();
  });

  // Corner filigree dots.
  g.fillStyle = "rgba(214,186,132,0.45)";
  for (const [fx, fy] of [[m2 * 1.6, m2 * 1.6], [w - m2 * 1.6, m2 * 1.6], [m2 * 1.6, h - m2 * 1.6], [w - m2 * 1.6, h - m2 * 1.6]]) {
    g.beginPath();
    g.arc(fx!, fy!, w * 0.014, 0, Math.PI * 2);
    g.fill();
  }
}

// ------------------------------------------------------------------- chips

/** Top-down chess piece: colored disc, subtle rim, piece glyph in ink. */
function drawPiece(g: CanvasRenderingContext2D, w: number, h: number, glyph: string, color: string, ink: string): void {
  const cx = w / 2, cy = h / 2, R = Math.min(w, h) / 2 - 1;
  g.beginPath();
  g.arc(cx, cy, R, 0, Math.PI * 2);
  g.fillStyle = color;
  g.fill();
  g.strokeStyle = ink;
  g.globalAlpha = 0.45;
  g.lineWidth = Math.max(1.5, R * 0.06);
  g.stroke();
  g.beginPath();
  g.arc(cx, cy, R * 0.82, 0, Math.PI * 2);
  g.lineWidth = Math.max(1, R * 0.03);
  g.stroke();
  g.globalAlpha = 1;
  g.fillStyle = ink;
  // DejaVu carries the chess block on Linux; the generic families cover the rest.
  g.font = `${R * 1.25}px "DejaVu Sans", "Segoe UI Symbol", serif, sans-serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(glyph, cx, cy + R * 0.06);
}

function drawChip(g: CanvasRenderingContext2D, w: number, h: number, color: string, value: number | string): void {
  const cx = w / 2, cy = h / 2, R = Math.min(w, h) / 2 - 1;
  g.beginPath();
  g.arc(cx, cy, R, 0, Math.PI * 2);
  g.fillStyle = color;
  g.fill();
  // Edge dashes.
  g.strokeStyle = "rgba(255,255,255,0.85)";
  g.lineWidth = R * 0.18;
  for (let i = 0; i < 6; i++) {
    const a0 = (i / 6) * Math.PI * 2 + 0.12;
    const a1 = a0 + Math.PI / 6 - 0.24;
    g.beginPath();
    g.arc(cx, cy, R * 0.88, a0, a1);
    g.stroke();
  }
  // Inner ring + value.
  g.beginPath();
  g.arc(cx, cy, R * 0.62, 0, Math.PI * 2);
  g.strokeStyle = "rgba(255,255,255,0.5)";
  g.lineWidth = 2;
  g.stroke();
  const light = isLight(color);
  g.fillStyle = light ? "#33322e" : "#f5f2ea";
  const text = String(value);
  g.font = `bold ${R * (text.length > 3 ? 0.34 : text.length > 2 ? 0.44 : 0.62)}px Arial, sans-serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(text, cx, cy + R * 0.02);
}

function isLight(hex: string): boolean {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255, gg = (n >> 8) & 255, b = n & 255;
  return 0.299 * r + 0.587 * gg + 0.114 * b > 150;
}

// ------------------------------------------------------------------ boards

function drawBoard(g: CanvasRenderingContext2D, w: number, h: number, style: string, color?: string): void {
  if (style === "trick") return drawTrickMat(g, w, h);
  if (style === "backgammon") return drawBackgammon(g, w, h);
  if (style === "checker") {
    const n = 8, m = w * 0.04, cell = (w - 2 * m) / n;
    g.fillStyle = "#e9dcc3";
    g.fillRect(0, 0, w, h);
    g.strokeStyle = "#6b4a2f";
    g.lineWidth = 3;
    g.strokeRect(m / 2, m / 2, w - m, h - m);
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        if ((x + y) % 2 === 1) {
          g.fillStyle = "#7a5230";
          g.fillRect(m + x * cell, m + y * cell, cell, cell);
        }
      }
    }
  } else {
    g.fillStyle = color ?? "#2c3e50";
    g.fillRect(0, 0, w, h);
    g.strokeStyle = "rgba(255,255,255,0.14)";
    g.lineWidth = 1;
    const step = w / 12;
    g.beginPath();
    for (let x = 0; x <= w; x += step) { g.moveTo(x, 0); g.lineTo(x, h); }
    for (let y = 0; y <= h; y += step) { g.moveTo(0, y); g.lineTo(w, y); }
    g.stroke();
  }
}

/**
 * Backgammon board face: wooden frame and bar, dark felt field, 24 points in
 * alternating colors. Point positions come from the shared BACKGAMMON spec —
 * the same one the table builder places checkers from — so triangles and
 * checkers cannot drift apart.
 */
function drawBackgammon(g: CanvasRenderingContext2D, w: number, h: number): void {
  const ux = w / BACKGAMMON.w, uz = h / BACKGAMMON.d;
  const f = BACKGAMMON.frame;
  // Wooden frame with a subtle grain gradient.
  const wood = g.createLinearGradient(0, 0, w, h);
  wood.addColorStop(0, "#6d4a2a");
  wood.addColorStop(1, "#553a20");
  g.fillStyle = wood;
  g.fillRect(0, 0, w, h);
  // Playfield felt.
  g.fillStyle = "#33291d";
  g.fillRect(f * ux, f * uz, w - 2 * f * ux, h - 2 * f * uz);
  // The 24 points, colors alternating continuously around the track.
  const pitch = bgPointPitch();
  for (let p = 1; p <= 24; p++) {
    const cx = (bgPointX(p) + BACKGAMMON.w / 2) * ux;
    const south = p <= 12;
    const baseZ = south ? h - f * uz : f * uz;
    const tipZ = south ? baseZ - BACKGAMMON.pointDepth * uz : baseZ + BACKGAMMON.pointDepth * uz;
    g.beginPath();
    g.moveTo(cx - (pitch / 2 - 0.2) * ux, baseZ);
    g.lineTo(cx + (pitch / 2 - 0.2) * ux, baseZ);
    g.lineTo(cx, tipZ);
    g.closePath();
    g.fillStyle = p % 2 ? "#ddceac" : "#a8452f";
    g.fill();
    g.strokeStyle = "rgba(0,0,0,0.3)";
    g.lineWidth = Math.max(1, 0.12 * ux);
    g.stroke();
  }
  // Bar over the points, with a hinge line down the middle.
  g.fillStyle = wood;
  g.fillRect((BACKGAMMON.w / 2 - BACKGAMMON.bar / 2) * ux, 0, BACKGAMMON.bar * ux, h);
  g.strokeStyle = "rgba(0,0,0,0.35)";
  g.lineWidth = Math.max(1, 0.15 * ux);
  g.beginPath();
  g.moveTo(w / 2, f * uz * 0.4);
  g.lineTo(w / 2, h - f * uz * 0.4);
  g.stroke();
  // Inner frame edge.
  g.strokeRect(f * ux, f * uz, w - 2 * f * ux, h - 2 * f * uz);
}

/**
 * Trick-taking mat face: green felt with one dashed card outline per seat,
 * drawn from the shared TRICK_MAT spec — the same one the server places the
 * trick slot zones from — plus a four-suit centerpiece.
 */
function drawTrickMat(g: CanvasRenderingContext2D, w: number, h: number): void {
  const ux = w / TRICK_MAT.size, uz = h / TRICK_MAT.size;
  roundRectPath(g, 0, 0, w, h, 2.4 * ux);
  const grad = g.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w * 0.7);
  grad.addColorStop(0, "#2a6a45");
  grad.addColorStop(1, "#1b4830");
  g.fillStyle = grad;
  g.fill();
  g.strokeStyle = "rgba(240,230,200,0.35)";
  g.lineWidth = Math.max(1.5, 0.35 * ux);
  g.stroke();
  // Inner border rope.
  roundRectPath(g, 1.6 * ux, 1.6 * uz, w - 3.2 * ux, h - 3.2 * uz, 1.8 * ux);
  g.strokeStyle = "rgba(240,230,200,0.15)";
  g.lineWidth = Math.max(1, 0.2 * ux);
  g.stroke();
  // Card outlines, one per seat, rotated to match the slot zone yaw.
  for (const sl of TRICK_MAT.slots) {
    g.save();
    g.translate((sl.lx + TRICK_MAT.size / 2) * ux, (sl.lz + TRICK_MAT.size / 2) * uz);
    g.rotate(sl.yaw);
    roundRectPath(g, (-TRICK_MAT.slotW / 2) * ux, (-TRICK_MAT.slotD / 2) * uz, TRICK_MAT.slotW * ux, TRICK_MAT.slotD * uz, 0.7 * ux);
    g.setLineDash([5, 4]);
    g.strokeStyle = "rgba(240,230,200,0.4)";
    g.lineWidth = Math.max(1, 0.24 * ux);
    g.stroke();
    g.setLineDash([]);
    g.fillStyle = "rgba(240,230,200,0.3)";
    g.font = `600 ${1.6 * ux}px system-ui, sans-serif`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText("PLAY", 0, (TRICK_MAT.slotD / 2 - 1.5) * uz);
    g.restore();
  }
  // Four-suit centerpiece diamond.
  const suits: Array<[string, string, number, number]> = [
    ["♠", "#e8e2d2", 0, -3.4],
    ["♥", "#d05555", 3.4, 0],
    ["♦", "#d05555", 0, 3.4],
    ["♣", "#e8e2d2", -3.4, 0],
  ];
  g.font = `${3.4 * ux}px system-ui, sans-serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  for (const [glyph, color, dx, dz] of suits) {
    g.fillStyle = color;
    g.globalAlpha = 0.35;
    g.fillText(glyph, (TRICK_MAT.size / 2 + dx) * ux, (TRICK_MAT.size / 2 + dz) * uz);
  }
  g.globalAlpha = 1;
}

/**
 * Commander playmat face: dark mat, a battlefield area, and a labeled slot
 * column (library / graveyard / exile / command). Slot boxes are drawn from
 * the shared MTG_MAT spec — the same one the server places slot zones from —
 * so the art and the drop zones cannot drift apart.
 */
/**
 * MTG playmat v2 — a flat printed mat. Slot boxes come from the shared
 * MTG_MAT spec (the same one the server places drop zones from), stacked in
 * a vertical column on the right; the rest is open battlefield with a faint
 * lands-row guide. The corner radius matches the def's cornerRadius so the
 * art fills the flat mesh exactly.
 */
function drawMtgMatV2(g: CanvasRenderingContext2D, w: number, h: number, accent?: string): void {
  const M = MTG_MAT;
  const ux = w / M.w, uz = h / M.d;
  const ac = accent ?? "#d8b45a";
  const r = 3 * ux; // = def cornerRadius

  // Base cloth: deep charcoal with a soft center glow, speckled like fabric.
  roundRectPath(g, 0, 0, w, h, r);
  g.save();
  g.clip();
  const grad = g.createRadialGradient(w * 0.42, h * 0.4, h * 0.1, w * 0.5, h * 0.55, w * 0.75);
  grad.addColorStop(0, "#262c38");
  grad.addColorStop(0.55, "#1e232e");
  grad.addColorStop(1, "#161a22");
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  // Deterministic speckle (cheap LCG) — reads as woven cloth, not banding.
  let seed = 41;
  const rnd = () => (seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
  g.fillStyle = "rgba(255,255,255,0.045)";
  for (let i = 0; i < 1400; i++) g.fillRect(rnd() * w, rnd() * h, 1.4, 1.4);
  g.fillStyle = "rgba(0,0,0,0.10)";
  for (let i = 0; i < 900; i++) g.fillRect(rnd() * w, rnd() * h, 1.6, 1.6);

  // Faint rings + mana star centered on the battlefield (left of the column).
  const divLocal = M.colX - M.slotW / 2 - 1.8; // mat-local x of the divider
  const bfCx = ((-M.w / 2 + divLocal) / 2 + M.w / 2) * ux;
  const bfCy = h / 2;
  g.strokeStyle = "rgba(216,180,90,0.10)";
  g.lineWidth = 1.5 * ux;
  for (const rad of [14, 19]) {
    g.beginPath();
    g.arc(bfCx, bfCy, rad * uz, 0, Math.PI * 2);
    g.stroke();
  }
  // Five mana nodes on the inner ring — the color pie, quietly.
  g.fillStyle = "rgba(216,180,90,0.16)";
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + (i / 5) * Math.PI * 2;
    g.beginPath();
    g.arc(bfCx + Math.cos(a) * 14 * uz, bfCy + Math.sin(a) * 14 * uz, 1.5 * uz, 0, Math.PI * 2);
    g.fill();
  }
  g.restore();

  // Double border: accent outer line + faint inner line, both rounded.
  g.strokeStyle = ac;
  g.globalAlpha = 0.75;
  g.lineWidth = Math.max(1.5, 0.35 * ux);
  roundRectPath(g, 0.5 * ux, 0.5 * uz, w - ux, h - uz, r * 0.9);
  g.stroke();
  g.globalAlpha = 0.18;
  g.lineWidth = Math.max(1, 0.2 * ux);
  roundRectPath(g, 1.8 * ux, 1.8 * uz, w - 3.6 * ux, h - 3.6 * uz, r * 0.7);
  g.stroke();
  g.globalAlpha = 1;

  // Zone column divider.
  const divX = (M.colX - M.slotW / 2 - 1.8 + M.w / 2) * ux;
  g.strokeStyle = "rgba(255,255,255,0.10)";
  g.lineWidth = Math.max(1, 0.2 * ux);
  g.beginPath();
  g.moveTo(divX, 3.2 * uz);
  g.lineTo(divX, h - 3.2 * uz);
  g.stroke();

  // Slot boxes: dark inset fill, accent-tinted stroke, glyph + label.
  const GLYPHS: Record<string, string> = { command: "✦", exile: "⊘", graveyard: "☠", library: "▤" };
  for (const sl of M.slots) {
    const x = (sl.lx + M.w / 2) * ux, y = (sl.lz + M.d / 2) * uz;
    const bw = M.slotW * ux, bh = M.slotD * uz;
    roundRectPath(g, x - bw / 2, y - bh / 2, bw, bh, 0.8 * ux);
    g.fillStyle = "rgba(0,0,0,0.30)";
    g.fill();
    g.strokeStyle = sl.slot === "command" ? ac : "rgba(255,255,255,0.30)";
    g.globalAlpha = sl.slot === "command" ? 0.8 : 1;
    g.lineWidth = Math.max(1, 0.22 * ux);
    g.stroke();
    g.globalAlpha = 1;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillStyle = sl.slot === "command" ? "rgba(216,180,90,0.6)" : "rgba(255,255,255,0.30)";
    g.font = `${2.6 * ux}px system-ui, sans-serif`;
    g.fillText(GLYPHS[sl.slot] ?? "◇", x, y - 0.8 * uz);
    g.font = `600 ${1.35 * ux}px system-ui, sans-serif`;
    g.fillText(sl.label.toUpperCase(), x, y + (M.slotD / 2) * uz - 1.5 * uz);
  }

  // Lands-row guide: a faint dashed line near the player's edge.
  const landsY = (8 + M.d / 2) * uz;
  g.strokeStyle = "rgba(255,255,255,0.10)";
  g.setLineDash([6, 5]);
  g.lineWidth = Math.max(1, 0.18 * ux);
  g.beginPath();
  g.moveTo(3.2 * ux, landsY);
  g.lineTo(divX - 1.5 * ux, landsY);
  g.stroke();
  g.setLineDash([]);
  g.fillStyle = "rgba(255,255,255,0.16)";
  g.font = `600 ${1.4 * ux}px system-ui, sans-serif`;
  g.textAlign = "left";
  g.textBaseline = "alphabetic";
  g.fillText("LANDS", 3.4 * ux, h - 3 * uz);
}

function drawMtgMat(g: CanvasRenderingContext2D, w: number, h: number, color?: string): void {
  const MTG_MAT = MTG_MAT_LEGACY; // legacy slab mat keeps its frozen layout
  const ux = w / MTG_MAT.w, uz = h / MTG_MAT.d;
  roundRectPath(g, 0, 0, w, h, 1.6 * ux);
  const grad = g.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, color ?? "#232833");
  grad.addColorStop(1, "#1b1f28");
  g.fillStyle = grad;
  g.fill();
  g.strokeStyle = "rgba(255,255,255,0.16)";
  g.lineWidth = Math.max(1.5, 0.3 * ux);
  g.stroke();

  const box = (lx: number, lz: number, bw: number, bh: number, label: string) => {
    const x = (lx + MTG_MAT.w / 2) * ux, y = (lz + MTG_MAT.d / 2) * uz;
    roundRectPath(g, x - (bw / 2) * ux, y - (bh / 2) * uz, bw * ux, bh * uz, 0.8 * ux);
    g.strokeStyle = "rgba(255,255,255,0.28)";
    g.setLineDash([5, 4]);
    g.lineWidth = Math.max(1, 0.22 * ux);
    g.stroke();
    g.setLineDash([]);
    g.fillStyle = "rgba(255,255,255,0.34)";
    g.font = `600 ${1.7 * ux}px system-ui, sans-serif`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText(label.toUpperCase(), x, y + (bh / 2) * uz - 1.6 * uz);
  };
  for (const sl of MTG_MAT.slots) box(sl.lx, sl.lz, MTG_MAT.slotW, MTG_MAT.slotD, sl.label);
  // Battlefield hint.
  g.fillStyle = "rgba(255,255,255,0.1)";
  g.font = `600 ${2.2 * ux}px system-ui, sans-serif`;
  g.textAlign = "left";
  g.fillText("BATTLEFIELD", 3 * ux, 3.4 * uz);
}

/**
 * Pokémon / Lorcana playmat faces. Slot boxes are drawn from the shared
 * POKE_MAT / LORCANA_MAT specs — the same ones the server places slot zones
 * from — so the art and the drop zones cannot drift apart.
 */
function drawTcgMat(g: CanvasRenderingContext2D, w: number, h: number, game: "pokemon" | "lorcana"): void {
  const mat: TcgMatSpec = game === "pokemon" ? POKE_MAT : LORCANA_MAT;
  const ux = w / mat.w, uz = h / mat.d;
  const [c0, c1, accent] =
    game === "pokemon"
      ? ["#2b3547", "#202836", "rgba(232,178,58,0.8)"]
      : ["#2c2450", "#1e1a38", "rgba(201,162,75,0.85)"];

  roundRectPath(g, 0, 0, w, h, 1.6 * ux);
  const grad = g.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, c0);
  grad.addColorStop(1, c1);
  g.fillStyle = grad;
  g.fill();
  g.strokeStyle = "rgba(255,255,255,0.16)";
  g.lineWidth = Math.max(1.5, 0.3 * ux);
  g.stroke();

  // Centerpiece watermark: pokéball ring / ink hex.
  const cx = w / 2, cy = h / 2;
  g.save();
  g.globalAlpha = 0.12;
  if (game === "pokemon") {
    const R = 9 * uz;
    g.strokeStyle = "#e8e2d2";
    g.lineWidth = 1.6 * uz;
    g.beginPath();
    g.arc(cx, cy, R, 0, Math.PI * 2);
    g.stroke();
    g.beginPath();
    g.moveTo(cx - R, cy);
    g.lineTo(cx + R, cy);
    g.stroke();
    g.beginPath();
    g.arc(cx, cy, R * 0.32, 0, Math.PI * 2);
    g.stroke();
  } else {
    const R = 9 * uz;
    g.strokeStyle = "#d8c893";
    g.lineWidth = 1.4 * uz;
    g.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i / 6) * Math.PI * 2;
      const px = cx + Math.cos(a) * R, py = cy + Math.sin(a) * R;
      i === 0 ? g.moveTo(px, py) : g.lineTo(px, py);
    }
    g.closePath();
    g.stroke();
    g.font = `${R * 1.1}px system-ui, sans-serif`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillStyle = "#d8c893";
    g.fillText("✦", cx, cy + R * 0.06);
  }
  g.restore();

  // Slot boxes + labels from the shared spec.
  for (const sl of mat.slots) {
    const x = (sl.lx + mat.w / 2) * ux, y = (sl.lz + mat.d / 2) * uz;
    roundRectPath(g, x - (mat.slotW / 2) * ux, y - (mat.slotD / 2) * uz, mat.slotW * ux, mat.slotD * uz, 0.8 * ux);
    g.strokeStyle = sl.slot === "active" ? accent : "rgba(255,255,255,0.28)";
    g.setLineDash([5, 4]);
    g.lineWidth = Math.max(1, 0.22 * ux);
    g.stroke();
    g.setLineDash([]);
    g.fillStyle = sl.slot === "active" ? accent : "rgba(255,255,255,0.34)";
    g.font = `600 ${1.5 * ux}px system-ui, sans-serif`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText(sl.label.toUpperCase(), x, y + (mat.slotD / 2) * uz - 1.5 * uz);
    if (sl.slot === "prize") g.fillText("×6", x, y - (mat.slotD / 2) * uz + 1.6 * uz);
  }
  // Corner hint.
  g.fillStyle = "rgba(255,255,255,0.12)";
  g.font = `600 ${2 * ux}px system-ui, sans-serif`;
  g.textAlign = "left";
  g.fillText(game === "pokemon" ? "POKÉMON" : "LORCANA — QUEST TO 20 LORE", 3 * ux, 3.2 * uz);
}

// --------------------------------------------------------------------- dice

/** Top-view polygon sides for each die (what its silhouette reads as). */
const DIE_TOP_SIDES: Record<number, number> = { 4: 3, 8: 4, 10: 5, 12: 5, 20: 6 };

/** Lighten (+) / darken (−) a hex color by an absolute channel amount. */
function shadeHex(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const c = (v: number) => Math.max(0, Math.min(255, v + amt));
  return `rgb(${c((n >> 16) & 255)},${c((n >> 8) & 255)},${c(n & 255)})`;
}

/**
 * One die face. d6: rounded square with pips (used for all six box faces in
 * 3D and the top face in 2D). Other dice: the die's top-down silhouette with
 * the rolled number — transparent outside the polygon.
 */
/** Pip layouts 0–6 in unit coords, shared by d6 faces and domino halves. */
const DIE_PIPS: Record<number, Array<[number, number]>> = {
  0: [],
  1: [[0.5, 0.5]],
  2: [[0.3, 0.3], [0.7, 0.7]],
  3: [[0.28, 0.28], [0.5, 0.5], [0.72, 0.72]],
  4: [[0.3, 0.3], [0.7, 0.3], [0.3, 0.7], [0.7, 0.7]],
  5: [[0.28, 0.28], [0.72, 0.28], [0.5, 0.5], [0.28, 0.72], [0.72, 0.72]],
  6: [[0.3, 0.26], [0.7, 0.26], [0.3, 0.5], [0.7, 0.5], [0.3, 0.74], [0.7, 0.74]],
};

/** Domino tile: ivory face, divider with a center pin, pips in each half. */
function drawDomino(g: CanvasRenderingContext2D, w: number, h: number, a: number, b: number): void {
  const r = w * 0.12;
  roundRectPath(g, 1, 1, w - 2, h - 2, r);
  const grad = g.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, "#f6efe0");
  grad.addColorStop(1, "#ddd2ba");
  g.fillStyle = grad;
  g.fill();
  g.strokeStyle = "#9a8f78";
  g.lineWidth = Math.max(1.5, w * 0.02);
  g.stroke();
  // Divider + brass center pin.
  const inset = w * 0.1;
  g.strokeStyle = "rgba(70,60,45,0.55)";
  g.lineWidth = Math.max(1.5, w * 0.018);
  g.beginPath();
  g.moveTo(inset, h / 2);
  g.lineTo(w - inset, h / 2);
  g.stroke();
  g.beginPath();
  g.ellipse(w / 2, h / 2, w * 0.035, w * 0.035, 0, 0, Math.PI * 2);
  g.fillStyle = "#a8863c";
  g.fill();
  // Pips: `a` in the top half, `b` in the bottom, each half a square region.
  g.fillStyle = "#2a2c33";
  const half = (n: number, y0: number) => {
    for (const [px, py] of DIE_PIPS[n] ?? []) {
      g.beginPath();
      g.ellipse(w * 0.14 + px * w * 0.72, y0 + h * 0.07 + py * (h / 2 - h * 0.14), w * 0.075, w * 0.075, 0, 0, Math.PI * 2);
      g.fill();
    }
  };
  half(a, 0);
  half(b, h / 2);
}

/** Domino back: dark "bone" tile with an ivory border and a spinner motif. */
function drawBoneBack(g: CanvasRenderingContext2D, w: number, h: number): void {
  const r = w * 0.12;
  roundRectPath(g, 1, 1, w - 2, h - 2, r);
  const grad = g.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, "#2b3138");
  grad.addColorStop(1, "#1d2126");
  g.fillStyle = grad;
  g.fill();
  g.strokeStyle = "#8f8674";
  g.lineWidth = Math.max(1.5, w * 0.02);
  g.stroke();
  const m = w * 0.11;
  roundRectPath(g, m, m, w - 2 * m, h - 2 * m, r * 0.6);
  g.strokeStyle = "rgba(220,208,180,0.35)";
  g.lineWidth = Math.max(1, w * 0.014);
  g.stroke();
  g.beginPath();
  g.ellipse(w / 2, h / 2, w * 0.13, w * 0.13, 0, 0, Math.PI * 2);
  g.strokeStyle = "rgba(220,208,180,0.5)";
  g.stroke();
  g.beginPath();
  g.ellipse(w / 2, h / 2, w * 0.045, w * 0.045, 0, 0, Math.PI * 2);
  g.fillStyle = "#a8863c";
  g.fill();
}

function drawDieFace(g: CanvasRenderingContext2D, w: number, h: number, sides: number, value: number, color: string, label?: string): void {
  if (sides === 6) {
    const r = w * 0.18;
    roundRectPath(g, w * 0.02, h * 0.02, w * 0.96, h * 0.96, r);
    const grad = g.createRadialGradient(w * 0.38, h * 0.34, w * 0.1, w / 2, h / 2, w * 0.75);
    grad.addColorStop(0, shadeHex(color, 18));
    grad.addColorStop(1, shadeHex(color, -22));
    g.fillStyle = grad;
    g.fill();
    g.strokeStyle = "rgba(0,0,0,0.35)";
    g.lineWidth = Math.max(1.5, w * 0.02);
    g.stroke();
    if (label) {
      // Labeled cube face (the doubling cube): the label instead of pips.
      g.fillStyle = "#8a2f25";
      g.font = `800 ${w * (label.length > 1 ? 0.42 : 0.52)}px system-ui, sans-serif`;
      g.textAlign = "center";
      g.textBaseline = "middle";
      g.fillText(label, w / 2, h * 0.54);
      return;
    }
    // Pips.
    g.fillStyle = "#23252b";
    for (const [px, py] of DIE_PIPS[value] ?? DIE_PIPS[1]!) {
      g.beginPath();
      g.ellipse(px * w, py * h, w * 0.075, w * 0.075, 0, 0, Math.PI * 2);
      g.fill();
    }
    return;
  }
  if (sides === 2) {
    // The coin: milled gold disc, HEADS (1) a star, TAILS (2) plain lettering.
    const cx = w / 2, cy = h / 2, R = Math.min(w, h) / 2 - 1;
    const grad = g.createRadialGradient(cx - R * 0.3, cy - R * 0.35, R * 0.2, cx, cy, R * 1.15);
    grad.addColorStop(0, shadeHex(color, 34));
    grad.addColorStop(1, shadeHex(color, -30));
    g.beginPath();
    g.arc(cx, cy, R, 0, Math.PI * 2);
    g.fillStyle = grad;
    g.fill();
    g.strokeStyle = "rgba(0,0,0,0.4)";
    g.lineWidth = Math.max(1.5, w * 0.02);
    g.stroke();
    g.beginPath();
    g.arc(cx, cy, R * 0.78, 0, Math.PI * 2);
    g.strokeStyle = "rgba(0,0,0,0.28)";
    g.lineWidth = Math.max(1, w * 0.015);
    g.stroke();
    g.fillStyle = "rgba(60,40,0,0.75)";
    g.textAlign = "center";
    g.textBaseline = "middle";
    if (value === 1) {
      g.font = `${R * 1.05}px system-ui, sans-serif`;
      g.fillText("★", cx, cy + R * 0.05);
      g.font = `700 ${R * 0.24}px system-ui, sans-serif`;
      g.fillText("HEADS", cx, cy + R * 0.62);
    } else {
      g.font = `800 ${R * 0.42}px system-ui, sans-serif`;
      g.fillText("TAILS", cx, cy + R * 0.02);
    }
    return;
  }
  // Polygonal dice: regular n-gon silhouette, point-up, facet lines, number.
  const n = DIE_TOP_SIDES[sides] ?? 6;
  const cx = w / 2, cy = h / 2, R = w * 0.47;
  g.beginPath();
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 + (i / n) * Math.PI * 2;
    pts.push([cx + Math.cos(a) * R, cy + Math.sin(a) * R]);
  }
  g.moveTo(pts[0]![0], pts[0]![1]);
  for (const [px, py] of pts.slice(1)) g.lineTo(px, py);
  g.closePath();
  const grad = g.createRadialGradient(cx - R * 0.25, cy - R * 0.3, R * 0.15, cx, cy, R * 1.15);
  grad.addColorStop(0, shadeHex(color, 26));
  grad.addColorStop(1, shadeHex(color, -30));
  g.fillStyle = grad;
  g.fill();
  g.strokeStyle = "rgba(0,0,0,0.4)";
  g.lineWidth = Math.max(1.5, w * 0.02);
  g.stroke();
  // Facet lines from each vertex toward the center.
  g.strokeStyle = "rgba(0,0,0,0.22)";
  g.lineWidth = Math.max(1, w * 0.012);
  for (const [px, py] of pts) {
    g.beginPath();
    g.moveTo(px, py);
    g.lineTo(cx + (px - cx) * 0.28, cy + (py - cy) * 0.28);
    g.stroke();
  }
  // The number (6/9 get the real-dice underline).
  g.fillStyle = "#f7f5ee";
  g.strokeStyle = "rgba(0,0,0,0.5)";
  g.lineWidth = w * 0.02;
  g.font = `800 ${w * (String(value).length > 1 ? 0.4 : 0.48)}px system-ui, sans-serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  const ty = cy + h * 0.03;
  g.strokeText(String(value), cx, ty);
  g.fillText(String(value), cx, ty);
  if (value === 6 || value === 9) {
    const uw = w * 0.14;
    g.fillRect(cx - uw / 2, ty + h * 0.21, uw, h * 0.03);
  }
}

const dieLabelCache = new Map<number, HTMLCanvasElement>();

/** Small round badge with the rolled value — the 3D value billboard. */
export function getDieLabel(value: number): HTMLCanvasElement {
  let c = dieLabelCache.get(value);
  if (c) return c;
  const S = 96;
  c = document.createElement("canvas");
  c.width = c.height = S;
  const g = c.getContext("2d")!;
  g.beginPath();
  g.ellipse(S / 2, S / 2, S * 0.46, S * 0.46, 0, 0, Math.PI * 2);
  g.fillStyle = "rgba(16,17,20,0.82)";
  g.fill();
  g.strokeStyle = "rgba(255,255,255,0.5)";
  g.lineWidth = S * 0.035;
  g.stroke();
  g.fillStyle = "#f7f5ee";
  g.font = `800 ${S * (String(value).length > 1 ? 0.42 : 0.52)}px system-ui, sans-serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(String(value), S / 2, S / 2 + S * 0.02);
  dieLabelCache.set(value, c);
  return c;
}

// -------------------------------------------------------- counter badges

const COUNTER_COLORS: Record<string, string> = {
  p1p1: "#2f9e44",
  m1m1: "#c0392b",
  loyalty: "#7048a8",
  lore: "#b8860b",
  defense: "#2465b0",
  generic: "#4a5060",
};

const counterCache = new Map<string, HTMLCanvasElement>();

/** Badge column for a card's counters (both renderers draw/texture this). */
export function getCounterBadges(counters: Array<{ id: string; label: string; n: number }>): HTMLCanvasElement {
  const key = JSON.stringify(counters);
  let c = counterCache.get(key);
  if (c) return c;
  const S = 4;
  const rowH = 22 * S, pad = 4 * S;
  c = document.createElement("canvas");
  const measure = c.getContext("2d")!;
  measure.font = `700 ${13 * S}px system-ui, sans-serif`;
  let maxW = 0;
  const texts = counters.map((ct) => {
    const t = ct.id === "p1p1" ? `+${ct.n}/+${ct.n}` : ct.id === "m1m1" ? `-${ct.n}/-${ct.n}` : `${ct.label} ${ct.n}`;
    maxW = Math.max(maxW, measure.measureText(t).width);
    return t;
  });
  c.width = Math.ceil(maxW + pad * 2.5);
  c.height = counters.length * (rowH + 2 * S) - 2 * S;
  const g = c.getContext("2d")!;
  g.font = `700 ${13 * S}px system-ui, sans-serif`;
  g.textBaseline = "middle";
  counters.forEach((ct, i) => {
    const y = i * (rowH + 2 * S);
    const color = COUNTER_COLORS[ct.id] ?? ct.id.startsWith("custom") ? COUNTER_COLORS.generic! : COUNTER_COLORS.generic!;
    roundRectPath(g, 0, y, c!.width, rowH, 6 * S);
    g.fillStyle = COUNTER_COLORS[ct.id] ?? "#4a5060";
    g.fill();
    g.strokeStyle = "rgba(0,0,0,0.4)";
    g.lineWidth = S;
    g.stroke();
    g.fillStyle = "#f5f2ea";
    g.textAlign = "center";
    g.fillText(texts[i]!, c!.width / 2, y + rowH / 2 + S / 2);
  });
  counterCache.set(key, c);
  if (counterCache.size > 300) counterCache.clear();
  return c;
}

// -------------------------------------------------------------- name tags

const nameCache = new Map<string, HTMLCanvasElement>();

/**
 * Oversampling factor for name tags: they are rendered at high resolution and
 * displayed smaller, so the text stays sharp even zoomed way in.
 */
export const TAG_SCALE = 8;

/** Seat label: colored dot + name on a dark pill (used on hand zones). */
export function getNameTag(name: string, color: string): HTMLCanvasElement {
  const key = `tag|${name}|${color}`;
  let c = nameCache.get(key);
  if (c) return c;
  const S = TAG_SCALE;
  c = document.createElement("canvas");
  const measure = c.getContext("2d")!;
  measure.font = `600 ${20 * S}px system-ui, sans-serif`;
  const tw = Math.ceil(measure.measureText(name).width);
  const padL = 26 * S, padR = 12 * S;
  c.width = tw + padL + padR;
  c.height = 34 * S;
  const g = c.getContext("2d")!;
  g.font = `600 ${20 * S}px system-ui, sans-serif`;
  // No stroke: hairline borders scale into fringe pixels at the corners.
  roundRectPath(g, 0, 0, c.width, c.height, 11 * S);
  g.fillStyle = "rgba(20,20,24,0.85)";
  g.fill();
  g.fillStyle = color;
  g.beginPath();
  g.arc(14 * S, c.height / 2, 6 * S, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = "#f2f0ea";
  g.textAlign = "left";
  g.textBaseline = "middle";
  g.fillText(name, padL, c.height / 2);
  nameCache.set(key, c);
  return c;
}

/**
 * Cursor label: name only, translucent — the hand itself carries the player
 * color, so no dot.
 */
export function getCursorTag(name: string): HTMLCanvasElement {
  const key = `cursor|${name}`;
  let c = nameCache.get(key);
  if (c) return c;
  const S = TAG_SCALE;
  c = document.createElement("canvas");
  const measure = c.getContext("2d")!;
  measure.font = `500 ${18 * S}px system-ui, sans-serif`;
  const tw = Math.ceil(measure.measureText(name).width);
  const pad = 10 * S;
  c.width = tw + pad * 2;
  c.height = 28 * S;
  const g = c.getContext("2d")!;
  g.font = `500 ${18 * S}px system-ui, sans-serif`;
  roundRectPath(g, 0, 0, c.width, c.height, 9 * S);
  g.fillStyle = "rgba(20,20,24,0.5)";
  g.fill();
  g.fillStyle = "rgba(242,240,234,0.92)";
  g.textAlign = "left";
  g.textBaseline = "middle";
  g.fillText(name, pad, c.height / 2);
  nameCache.set(key, c);
  return c;
}

// ------------------------------------------------------------ hand cursor

/**
 * Player cursor: an open hand, from Lucide's "hand" icon.
 * lucide.dev — ISC license (c) Lucide contributors.
 */
const HAND_PATHS = [
  "M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2",
  "M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2",
  "M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8",
  "M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15",
];

/** Where the "pointing spot" of the hand sits in the icon's 24×24 viewBox. */
export const HAND_ANCHOR = { x: 12, y: 3 };
export const HAND_SIZE = 132; // canvas px (rendered smaller for crispness)

const handCache = new Map<string, HTMLCanvasElement>();

export function getHandCursor(color: string): HTMLCanvasElement {
  let c = handCache.get(color);
  if (c) return c;

  // 1) The colored hand on its own layer.
  const base = document.createElement("canvas");
  base.width = base.height = HAND_SIZE;
  {
    const g = base.getContext("2d")!;
    const s = HAND_SIZE / 28; // small margin around the 24-unit viewBox
    g.translate((HAND_SIZE - 24 * s) / 2, (HAND_SIZE - 24 * s) / 2);
    g.scale(s, s);
    g.lineCap = "round";
    g.lineJoin = "round";
    g.strokeStyle = color;
    g.lineWidth = 2.6;
    for (const d of HAND_PATHS) g.stroke(new Path2D(d));
  }

  // 2) White silhouette of it (for an OUTSIDE-only outline via dilation —
  //    stroking a halo directly would put white between the fingers too).
  const sil = document.createElement("canvas");
  sil.width = sil.height = HAND_SIZE;
  {
    const g = sil.getContext("2d")!;
    g.drawImage(base, 0, 0);
    g.globalCompositeOperation = "source-in";
    g.fillStyle = "#f5f2ea";
    g.fillRect(0, 0, HAND_SIZE, HAND_SIZE);
  }

  // 3) Composite: dilated white silhouette underneath, colored hand on top.
  c = document.createElement("canvas");
  c.width = c.height = HAND_SIZE;
  const g = c.getContext("2d")!;
  const o = HAND_SIZE / 44; // outline thickness
  g.shadowColor = "rgba(0,0,0,0.4)";
  g.shadowBlur = HAND_SIZE / 30;
  g.shadowOffsetY = HAND_SIZE / 44;
  for (const [dx, dy] of [[-o, 0], [o, 0], [0, -o], [0, o], [-o, -o], [o, -o], [-o, o], [o, o]]) {
    g.drawImage(sil, dx!, dy!);
    g.shadowColor = "transparent"; // only the first stamp casts the shadow
  }
  g.drawImage(base, 0, 0);
  handCache.set(color, c);
  return c;
}
